function encodeChannelName(laravelChannel) {
  return String(laravelChannel).replace(/_/g, '-x')
}

function toBase64Url(value) {
  const json = typeof value === 'string' ? value : JSON.stringify(value)
  const base64 = btoa(json)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function extractHost(urlOrHost) {
  if (!urlOrHost) return ''
  try {
    if (urlOrHost.includes('://')) {
      return new URL(urlOrHost).host
    }
  } catch (e) {
    // fall through
  }
  return String(urlOrHost).replace(/^https?:\/\//, '').replace(/\/.*$/, '')
}

function parseEventPayload(rawEvent) {
  const candidates = Array.isArray(rawEvent) ? rawEvent : [rawEvent]
  for (let i = 0; i < candidates.length; i++) {
    const item = candidates[i]
    if (item == null) continue
    if (typeof item === 'object') return item
    if (typeof item === 'string') {
      try {
        return JSON.parse(item)
      } catch (e) {
        return { event: 'message', data: item }
      }
    }
  }
  return null
}

function createChannel(client, laravelChannelName) {
  const handlers = {}
  let subscriptionId = null
  let subscribed = false

  return {
    name: laravelChannelName,
    bind(eventName, callback) {
      if (!handlers[eventName]) handlers[eventName] = []
      handlers[eventName].push(callback)
    },
    unbind(eventName, callback) {
      if (!handlers[eventName]) return
      if (!callback) {
        delete handlers[eventName]
        return
      }
      handlers[eventName] = handlers[eventName].filter((fn) => fn !== callback)
      if (handlers[eventName].length === 0) delete handlers[eventName]
    },
    unsubscribe() {
      client._unsubscribeChannel(laravelChannelName)
    },
    _setSubscriptionId(id) {
      subscriptionId = id
      subscribed = true
    },
    _getSubscriptionId() {
      return subscriptionId
    },
    _isSubscribed() {
      return subscribed
    },
    _dispatch(envelope) {
      if (!envelope || typeof envelope !== 'object') return
      const eventName = envelope.event
      const payload = Object.prototype.hasOwnProperty.call(envelope, 'data')
        ? envelope.data
        : envelope
      const list = handlers[eventName]
      if (!list || !list.length) return
      list.slice().forEach((fn) => {
        try {
          fn(payload)
        } catch (err) {
          console.error('AppSync channel handler error', eventName, err)
        }
      })
    },
    _clear() {
      subscribed = false
      subscriptionId = null
    }
  }
}

export function createAppSyncEventsClient(options = {}) {
  const httpHost = extractHost(options.http || process.env.VUE_APP_APPSYNC_HTTP)
  const realtimeHost = extractHost(options.realtime || process.env.VUE_APP_APPSYNC_REALTIME)
  const apiKey = options.apiKey || process.env.VUE_APP_APPSYNC_API_KEY || ''
  const namespace = (options.namespace || process.env.VUE_APP_APPSYNC_NAMESPACE || 'default').replace(/^\/|\/$/g, '')

  const channels = {}
  let socket = null
  let ready = false
  let connecting = false
  let reconnectTimer = null
  let shouldReconnect = true
  let subSeq = 0

  const authorization = () => ({
    host: httpHost,
    'x-api-key': apiKey
  })

  function appsyncChannelPath(laravelChannelName) {
    return `/${namespace}/${encodeChannelName(laravelChannelName)}`
  }

  function nextSubId() {
    subSeq += 1
    return `sub-${Date.now()}-${subSeq}`
  }

  function send(message) {
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify(message))
  }

  function subscribeOnSocket(laravelChannelName, channel) {
    const id = nextSubId()
    channel._setSubscriptionId(id)
    send({
      type: 'subscribe',
      id,
      channel: appsyncChannelPath(laravelChannelName),
      authorization: authorization()
    })
  }

  function flushSubscriptions() {
    Object.keys(channels).forEach((name) => {
      const channel = channels[name]
      if (!channel._isSubscribed()) {
        subscribeOnSocket(name, channel)
      }
    })
  }

  function handleMessage(raw) {
    let message
    try {
      message = JSON.parse(raw)
    } catch (e) {
      return
    }

    if (message.type === 'connection_ack') {
      ready = true
      flushSubscriptions()
      return
    }

    if (message.type === 'data') {
      const envelope = parseEventPayload(message.event)
      if (!envelope) return
      const channel = Object.keys(channels)
        .map((name) => channels[name])
        .find((ch) => ch._getSubscriptionId() === message.id)
      if (channel) channel._dispatch(envelope)
      return
    }

    if (message.type === 'subscribe_error' || message.type === 'broadcast_error') {
      console.error('AppSync Events error', message)
    }
  }

  function connect() {
    if (connecting || (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING))) {
      return
    }
    if (!httpHost || !realtimeHost || !apiKey) {
      console.error('AppSync Events client missing VUE_APP_APPSYNC_* env')
      return
    }

    connecting = true
    ready = false

    const headerProtocol = `header-${toBase64Url(authorization())}`
    const url = `wss://${realtimeHost}/event/realtime`

    try {
      socket = new WebSocket(url, [headerProtocol, 'aws-appsync-event-ws'])
    } catch (err) {
      connecting = false
      console.error('AppSync Events WebSocket create failed', err)
      scheduleReconnect()
      return
    }

    socket.onopen = () => {
      connecting = false
      send({ type: 'connection_init' })
    }

    socket.onmessage = (evt) => {
      handleMessage(evt.data)
    }

    socket.onerror = () => {
      connecting = false
    }

    socket.onclose = () => {
      ready = false
      connecting = false
      socket = null
      Object.keys(channels).forEach((name) => {
        channels[name]._clear()
      })
      if (shouldReconnect) scheduleReconnect()
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer || !shouldReconnect) return
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, 2000)
  }

  function subscribe(laravelChannelName) {
    if (channels[laravelChannelName]) {
      return channels[laravelChannelName]
    }
    const channel = createChannel(client, laravelChannelName)
    channels[laravelChannelName] = channel
    connect()
    if (ready) {
      subscribeOnSocket(laravelChannelName, channel)
    }
    return channel
  }

  function unsubscribeChannel(laravelChannelName) {
    const channel = channels[laravelChannelName]
    if (!channel) return
    const id = channel._getSubscriptionId()
    if (id) {
      send({ type: 'unsubscribe', id })
    }
    channel._clear()
    delete channels[laravelChannelName]
  }

  function disconnect() {
    shouldReconnect = false
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    Object.keys(channels).forEach((name) => {
      unsubscribeChannel(name)
    })
    if (socket) {
      try {
        socket.close()
      } catch (e) {
        // ignore
      }
      socket = null
    }
    ready = false
    connecting = false
  }

  const client = {
    subscribe,
    disconnect,
    connection: {
      bind() {},
      unbind() {}
    },
    _unsubscribeChannel: unsubscribeChannel,
    _encodeChannelName: encodeChannelName,
    _appsyncChannelPath: appsyncChannelPath
  }

  return client
}

export { encodeChannelName, parseEventPayload, extractHost }
